export const brand = {
  name: '50 Mapas Visuales de Electroterapia',
  promise: '+300 Preguntas Visuales',
  promiseAccent: 'de Anatomía Veterinaria',
  subtitle:
    'Poné a prueba lo que realmente recordás antes del parcial. Respondé preguntas visuales de perros y gatos, revisá tus respuestas con una guía comentada y descubrí qué estructuras, sistemas y relaciones anatómicas todavía necesitás reforzar.',
  mockupCaption: '50 Mapas Visuales de Posicionamiento de Electrodos + 4 Bonos Profesionales',
}

export const heroBadges: string[] = ['Acceso inmediato', 'Imprimible', 'Tuyo de por vida']

export interface Stat {
  value: string
  label: string
}

export const stats: Stat[] = [
  { value: '300', label: 'preguntas visuales' },
  { value: '6', label: 'bloques de Anatomía' },
  { value: '300', label: 'respuestas comentadas' },
  { value: '5', label: 'bonos de repaso' },
]

export const proofNote = 'Un sistema visual de práctica para estudiantes de Veterinaria'

export const universities: string[] = ['Perros', 'Gatos', 'Anatomía por sistemas', 'Repaso activo']

export interface PainGroup {
  audience: string
  points: string[]
}

export const painGroups: PainGroup[] = [
  {
    audience: 'Si estudiás Veterinaria',
    points: [
      'Leés Anatomía durante horas, pero cuando cerrás los apuntes no sabés cuánto realmente recordás.',
      'Reconocés una estructura cuando la ves explicada, pero en el parcial dudás del nombre.',
      'Confundís músculos, huesos, órganos o relaciones anatómicas que se parecen.',
    ],
  },
  {
    audience: 'Cuando se acerca el examen',
    points: [
      'Estudiás todo de nuevo porque no sabés exactamente dónde están tus errores.',
      'Llegás al examen sin haber comprobado antes qué temas tenés realmente dominados.',
    ],
  },
]

export const solutionPoints: string[] = [
  '+300 preguntas visuales: comprobá cuánto realmente recordás.',
  '300 respuestas comentadas: no solo sabés cuál era correcta; entendés por qué.',
  'Organizado por sistemas: practicá exactamente el área que estás estudiando.',
  'Perros y gatos: trabajá con las dos especies durante todo el material.',
  'Registro de errores: identificá rápidamente qué temas necesitan refuerzo.',
  '5 bonos de repaso: herramientas extra para identificar, diferenciar y preparar el examen.',
]

export interface Module {
  number: string
  title: string
  count: string
  detail: string
  summary: string
  closing: string
  spine: string
}

export const modules: Module[] = [
  {
    number: '01',
    title: 'Orientación, Osteología y Articulaciones',
    count: '50',
    detail: '50 preguntas',
    summary:
      'Terminología anatómica, planos, regiones, cráneo, columna, miembros, pelvis, huesos y principales articulaciones.',
    closing: 'Construí una base sólida antes de avanzar hacia sistemas más complejos.',
    spine: 'bg-blue-400',
  },
  {
    number: '02',
    title: 'Miología',
    count: '50',
    detail: '50 preguntas',
    summary:
      'Músculos de cabeza, cuello, tronco, miembros torácicos y pelvianos, acciones y relaciones anatómicas.',
    closing: 'Identificá músculos directamente sobre imágenes y aprendé a diferenciarlos.',
    spine: 'bg-blue-600',
  },
  {
    number: '03',
    title: 'Sistema Cardiovascular y Respiratorio',
    count: '50',
    detail: '50 preguntas',
    summary:
      'Corazón, cámaras, grandes vasos, circulación, laringe, tráquea, bronquios, pulmones y relaciones torácicas.',
    closing:
      'Poné a prueba estructuras que suelen estudiarse de forma aislada y ahora aparecen en contexto.',
    spine: 'bg-blue-700',
  },
  {
    number: '04',
    title: 'Sistema Digestivo',
    count: '50',
    detail: '50 preguntas',
    summary:
      'Cavidad oral, esófago, estómago, intestinos, hígado, vías biliares, páncreas y topografía abdominal.',
    closing: 'Practicá identificación y relaciones anatómicas de todo el sistema digestivo.',
    spine: 'bg-blue-900',
  },
  {
    number: '05',
    title: 'Sistema Urinario y Reproductor',
    count: '50',
    detail: '50 preguntas',
    summary:
      'Riñones, uréteres, vejiga, aparato reproductor masculino y femenino y diferencias entre especies.',
    closing: 'Repasá estructuras y relaciones que suelen confundirse entre perro y gato.',
    spine: 'bg-blue-600',
  },
  {
    number: '06',
    title: 'Sistema Nervioso y Órganos de los Sentidos',
    count: '50',
    detail: '50 preguntas',
    summary: 'Encéfalo, médula, nervios principales, ojo, oído, olfato y estructuras asociadas.',
    closing: 'Terminá el recorrido comprobando uno de los bloques más exigentes de Anatomía.',
    spine: 'bg-blue-900',
  },
]

export const mapIncludes: string[] = [
  'Corriente o modalidad utilizada',
  'Región anatómica',
  'Grupo muscular u objetivo',
  'Electrodos identificados',
  'Canales y pares',
  'Posicionamiento visual',
  'Respuesta buscada',
  'Puntos de atención y precauciones',
]

export interface GalleryItem {
  title: string
  caption: string
  image: string
}

export const gallery: GalleryItem[] = [
  {
    title: 'Identificación de estructuras',
    caption:
      'Cada página combina imágenes anatómicas de perros y gatos con preguntas de selección múltiple para que puedas responder sin consultar primero la teoría.',
    image: 'https://cdn.magicpatterns.com/uploads/94Ga3NDW6VzR1esddc3ZDR/1.png',
  },
  {
    title: 'Orientación anatómica',
    caption:
      'Vas a encontrar preguntas sobre orientación anatómica, con imágenes de perros y gatos y opciones de selección múltiple.',
    image: 'https://cdn.magicpatterns.com/uploads/bR6UsnTUaUEpSDAwJ6iwCV/ejemolo1.png',
  },
  {
    title: 'Huesos y articulaciones',
    caption:
      'Vas a encontrar preguntas sobre huesos y articulaciones, con imágenes de perros y gatos y opciones de selección múltiple.',
    image: 'https://cdn.magicpatterns.com/uploads/4GuW9wntmpSSn7SMcomY2s/4.png',
  },
  {
    title: 'Músculos',
    caption:
      'Vas a encontrar preguntas sobre músculos, con imágenes de perros y gatos y opciones de selección múltiple.',
    image: 'https://cdn.magicpatterns.com/uploads/hPNVNk4UKUrfBgu35QHaGV/3.png',
  },
  {
    title: 'Relaciones topográficas',
    caption:
      'Vas a encontrar preguntas sobre relaciones topográficas, con imágenes de perros y gatos y opciones de selección múltiple.',
    image: 'https://cdn.magicpatterns.com/uploads/ganLTNERfbk8xqXURrrQPH/5.png',
  },
  {
    title: 'Órganos, sistemas y diferencias entre perro y gato',
    caption:
      'Vas a encontrar preguntas sobre órganos y sistemas, y sobre las diferencias entre perro y gato.',
    image: 'https://cdn.magicpatterns.com/uploads/avmFTM5eycNR8iS58h662f/6.png',
  },
]

export interface Bonus {
  tag: string
  title: string
  subtitle: string
  value: string
  benefit: string
  detail: string
  image: string
}

export const bonusesTotalValue = '$28.950'

export const bonuses: Bonus[] = [
  {
    tag: 'Bono 01',
    title: 'Atlas Mudo Veterinario',
    subtitle: 'Láminas para identificar estructuras sin pistas',
    value: '$6.990',
    benefit:
      'Practicá anatomía señalando y nombrando estructuras antes de revisar las referencias. Ideal para pasar de “lo reconozco cuando lo veo” a “puedo identificarlo sin ayuda”.',
    detail: 'Láminas para identificar estructuras sin pistas',
    image: 'https://cdn.magicpatterns.com/uploads/n4umKkTPBhNYMxWC9ynodQ/image.png',
  },
  {
    tag: 'Bono 02',
    title: 'No las Confundas',
    subtitle: 'Estructuras anatómicas parecidas, comparadas visualmente',
    value: '$5.990',
    benefit:
      'Una colección de pares y grupos que suelen generar dudas entre estudiantes. Compará ubicación, forma, relaciones y características visuales clave.',
    detail: 'Estructuras anatómicas parecidas, comparadas visualmente',
    image: 'https://cdn.magicpatterns.com/uploads/3wrwYbTHasbc9fprKVaz6g/image.png',
  },
  {
    tag: 'Bono 03',
    title: 'Simulacros de Parcial',
    subtitle: 'Practicá con preguntas mezcladas como antes de un examen',
    value: '$5.990',
    benefit:
      'Simulacros para combinar temas de distintos bloques y comprobar cómo respondés cuando ya no sabés de qué sistema viene la pregunta. Incluye preguntas mixtas, puntuación, corrección y registro de errores.',
    detail: 'Preguntas mixtas, puntuación, corrección y registro de errores',
    image: 'https://cdn.magicpatterns.com/uploads/oKhdrsvTXwoKTiscwZ1Fhc/image.png',
  },
  {
    tag: 'Bono 04',
    title: 'Panel de Errores por Sistemas',
    subtitle: 'Descubrí qué área necesitás reforzar primero',
    value: '$4.990',
    benefit:
      'Registrá tus resultados de Osteología, Miología, Cardiovascular, Digestivo, Urinario/Reproductor y Nervioso. Visualizá rápidamente qué bloque tenés más firme y cuál necesita repaso.',
    detail: 'Descubrí qué área necesitás reforzar primero',
    image: 'https://cdn.magicpatterns.com/uploads/tAYmUQd9UeMgiaUWWYkpPS/image.png',
  },
  {
    tag: 'Bono 05',
    title: 'Diccionario Visual Express',
    subtitle: 'Los términos que aparecen una y otra vez en Anatomía',
    value: '$4.990',
    benefit:
      'Una referencia rápida para consultar conceptos como craneal, caudal, medial, lateral, proximal, distal, dorsal, ventral, superficial, profundo y otros términos anatómicos esenciales.',
    detail: 'Los términos que aparecen una y otra vez en Anatomía',
    image: 'https://cdn.magicpatterns.com/uploads/3L6CnwoKELqnWxPSzZCvVh/image.png',
  },
]

export interface Testimonial {
  initials: string
  quote: string
  author: string
  role: string
  city: string
}

export const testimonials: Testimonial[] = [
  {
    initials: 'LM',
    quote:
      'Anatomía me costaba porque sentía que entendía cuando leía, pero después sin los apuntes dudaba de todo. Con las preguntas visuales pude darme cuenta rápido de qué temas realmente tenía que volver a repasar.',
    author: 'Lucía M.',
    role: 'Estudiante de Veterinaria',
    city: 'La Plata',
  },
  {
    initials: 'MC',
    quote:
      'Lo que más me sirvió fue practicar por bloques. Termino una ronda, reviso las respuestas comentadas y ya sé exactamente en qué estructuras me estoy equivocando. Es mucho más práctico que releer todo otra vez.',
    author: 'Martín C.',
    role: 'Estudiante de Veterinaria',
    city: 'CABA',
  },
  {
    initials: 'FS',
    quote:
      'Antes del parcial hice varias rondas sin mirar mis apuntes y encontré errores que pensaba que ya tenía resueltos. Las imágenes de perros y gatos me ayudaron muchísimo a reconocer las estructuras con más seguridad.',
    author: 'Florencia S.',
    role: 'Estudiante de Veterinaria',
    city: 'Córdoba',
  },
]

export interface FaqItem {
  question: string
  answer: string
}

export const faqs: FaqItem[] = [
  {
    question: '¿Cuándo recibo el material?',
    answer:
      'El acceso es inmediato después de confirmar el pago. Recibís los archivos digitales para comenzar a practicar desde tu celular, tablet o computadora.',
  },
  {
    question: '¿Sirve si estoy estudiando Veterinaria?',
    answer:
      'Sí. El material está pensado principalmente para estudiantes de Medicina Veterinaria que quieren practicar y repasar Anatomía de perros y gatos.',
  },
  {
    question: '¿Tengo que haber estudiado Anatomía antes?',
    answer:
      'El material funciona mejor como herramienta de práctica y repaso. No reemplaza tus clases ni apuntes: te ayuda a comprobar qué recordás y qué necesitás volver a estudiar.',
  },
  {
    question: '¿Las preguntas tienen respuesta?',
    answer:
      'Sí. Recibís una Guía de 300 Respuestas Comentadas para corregir después de completar cada bloque.',
  },
  {
    question: '¿Solo incluye perros?',
    answer:
      'No. El material trabaja con perros y gatos e incluye también comparaciones entre ambas especies.',
  },
  {
    question: '¿Qué temas incluye?',
    answer:
      'Incluye seis bloques: Orientación/Osteología/Articulaciones, Miología, Cardiovascular/Respiratorio, Digestivo, Urinario/Reproductor y Nervioso/Órganos de los Sentidos.',
  },
  {
    question: '¿Puedo verlo desde el celular?',
    answer: 'Sí. Los archivos son digitales y pueden utilizarse desde celular, tablet o computadora.',
  },
  {
    question: '¿Puedo imprimirlo?',
    answer:
      'Sí. Está preparado en formato PDF y podés imprimir las páginas que quieras utilizar en papel.',
  },
  {
    question: '¿El acceso es de por vida?',
    answer: 'Sí. Es un pago único y el acceso al material es de por vida.',
  },
  {
    question: '¿Sirve para preparar parciales?',
    answer:
      'Está pensado precisamente como herramienta de práctica y repaso previo a evaluaciones. No garantiza un resultado académico, pero te permite detectar errores antes del examen.',
  },
  {
    question: '¿Qué pasa si no me sirve?',
    answer: 'Tenés 7 días de garantía para solicitar el reembolso.',
  },
]

export const scarcity = {
  total: 50,
  left: 42,
  note: 'Precio especial para los primeros 50.',
  nextPrice: '$24.990',
}

export const checkoutUrl = 'https://veterinaria.impultienda.ar'

export interface Offer {
  name: string
  summary: string
  finalSummary: string
  includes: string[]
  listPrice: string
  totalValue: string
  price: string
  discount: string
  savings: string
  cta: string
  finalCta: string
}

export const offer: Offer = {
  name: '50 Mapas Visuales de Electroterapia — Sistema Completo',
  summary: '50 mapas + Atlas de 100 puntos motores + 3 herramientas profesionales',
  finalSummary: '50 mapas visuales + 4 bonos profesionales',
  includes: [
    '50 Mapas Visuales de Posicionamiento de Electrodos',
    'TENS, NMES, FES e interferencial',
    'Miembros superiores e inferiores',
    'Columna, tronco, hombro y cadera',
    'Electrodos, pares y canales identificados',
    'Aplicaciones funcionales',
    'Guía Rápida de Parámetros',
    'Checklist de Seguridad y Contraindicaciones',
    'Atlas Visual de Puntos Motores',
    'Fichas de Consulta Rápida por Corriente',
    'PDF en alta calidad',
    'Imprimible',
    'Acceso de por vida',
  ],
  listPrice: '$29.990',
  totalValue: '$71.930',
  price: '$14.990',
  discount: '62% off',
  savings: '$25.000',
  cta: 'Quiero las +300 Preguntas + 5 Bonos',
  finalCta: 'Quiero acceder al sistema completo',
}

export const pricingRows: { label: string; value: string }[] = [
  { label: '+300 Preguntas Visuales de Anatomía Veterinaria', value: '$29.990' },
  { label: 'Guía de 300 Respuestas Comentadas', value: '$12.990' },
  { label: '5 Bonos de Repaso', value: '$28.950' },
]

export const heroImage =
  'https://cdn.magicpatterns.com/uploads/oqoFuw2dbKmGjmkFiFAu5T/mockup-landing.png'

export interface PurchaseNotice {
  name: string
  city: string
  product: string
}

export const purchaseNotices: PurchaseNotice[] = [
  { name: 'Camila R.', city: 'Córdoba', product: '+300 Preguntas Visuales + 5 bonos' },
  { name: 'Nicolás B.', city: 'Rosario', product: '+300 Preguntas Visuales + 5 bonos' },
  { name: 'Sofía D.', city: 'La Plata', product: '+300 Preguntas Visuales + 5 bonos' },
]

export const footerLinks: { label: string; href: string }[] = [
  { label: 'Términos de Servicio', href: '#terminos' },
  { label: 'Política de Privacidad', href: '#privacidad' },
  { label: 'Contacto', href: '#contacto' },
]
